/**
 * Audit log schemas — unified surface over django-easy-audit's three tables:
 *   - CRUDEvent (create/update/delete on tracked models)
 *   - LoginEvent (login / logout / failed_login)
 *   - RequestEvent (HTTP requests — verbose, usually filtered out by default)
 *
 * The frontend treats them as a single `AuditEvent` discriminated union so
 * the table can render a unified feed with mixed event types.
 */
import { z } from "zod";

/** What kind of event happened. */
const AuditEventKindSchema = z.enum(["create", "update", "delete", "login", "logout", "login_failed", "request"]);
export type AuditEventKind = z.infer<typeof AuditEventKindSchema>;

/**
 * One row in the audit log feed.
 *
 * Fields are inclusive of all three underlying event types — only the
 * relevant subset will be populated for any given row. This keeps the
 * frontend logic simple at the cost of some optional fields per row.
 */
const AuditEventSchema = z.object({
  /** UUID assigned by the backend when persisting the event. */
  id: z.string(),
  kind: AuditEventKindSchema,
  /** ISO timestamp the event occurred. */
  occurredAt: z.string(),
  /** Username, or null for unauthenticated events (e.g. failed login). */
  user: z.string().nullable(),
  /** Vendor scope for the event (meraki / aruba / shasta / system). */
  vendor: z.enum(["meraki", "aruba", "shasta", "system"]).optional(),
  /** Free-form short description shown in the table cell. */
  summary: z.string(),
  /** Optional structured detail for the expander row. Shape varies by kind. */
  detail: z.record(z.unknown()).optional(),
  /** Source IP for login/request events. */
  remoteIp: z.string().nullable().optional(),
  /** For CRUD events: model name + record id. */
  modelName: z.string().nullable().optional(),
  recordId: z.string().nullable().optional(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;
const AuditEventListSchema = z.array(AuditEventSchema);

/** Paginated wrapper — mirrors the standard DRF page envelope. */
export const AuditPageSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: AuditEventListSchema,
});
export type AuditPage = z.infer<typeof AuditPageSchema>;
